'use client';

import { c as _c } from "react/compiler-runtime";
import './volunteer-action-dialog.scss';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ButtonStyleProps } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { CheckboxGroupButtons, CheckboxGroupButtonsItem } from '@sola/ark-core/molecules/checkbox-group-buttons/checkbox-group-buttons.tsx';
import { Dialog, DialogActionClose, DialogActionPrimary, DialogActions, DialogContent, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import type { GraphQLVolunteerSlot } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { VolunteerSlotContact } from '@sola/shared-utils/supporters/volunteers.ts';
import { useVolunteerActionDialog } from './volunteer-action-dialog.logic.tsx';
export type VolunteerAction = 'cancel' | 'confirm';
export type VolunteerActionDialogProps = {
  action?: VolunteerAction;
  contacts: VolunteerSlotContact[];
  onOpenChange?: (open: boolean) => void;
  onSubmit: (slots: GraphQLVolunteerSlot[]) => Promise<void> | void;
  open?: boolean;
  openTrigger?: ReactElement<ButtonStyleProps>;
  slots: GraphQLVolunteerSlot[];
};
export const VolunteerActionDialog: React.FC<VolunteerActionDialogProps> = props => {
  const $ = _c(56);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["volunteers", "my"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    i18n,
    t
  } = useTranslation(t0);
  const language = i18n.resolvedLanguage ?? i18n.language;
  const {
    action: t1,
    openTrigger
  } = props;
  const action = t1 === undefined ? "cancel" : t1;
  const {
    checkboxGroupId,
    contactOptions,
    handleOpenChange,
    handleSubmit,
    hasMultipleContacts,
    isBusy,
    isCancelAction,
    isDialogOpen,
    isSubmitDisabled,
    selectedContactIds,
    setSelectedContactIds,
    shiftDetails,
    showSubmitError,
    titleKey,
    translationCount,
    translationKey
  } = useVolunteerActionDialog(props, language);
  let t2;
  if ($[1] !== openTrigger) {
    t2 = openTrigger && <DialogTrigger>{openTrigger}</DialogTrigger>;
    $[1] = openTrigger;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `VolunteerActionDialog VolunteerActionDialog--${action}`;
  const t4 = isCancelAction ? "user-x-02" : "users-check";
  const t5 = isCancelAction ? "negative" : "neutral";
  const t6 = `${translationKey}.${titleKey}`;
  let t7;
  if ($[3] !== t || $[4] !== t6 || $[5] !== translationCount) {
    t7 = t(t6, {
      count: translationCount
    });
    $[3] = t;
    $[4] = t6;
    $[5] = translationCount;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== shiftDetails) {
    t8 = shiftDetails.map(_temp);
    $[7] = shiftDetails;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t8) {
    t9 = <span className="flex flex-col">{t8}</span>;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== showSubmitError || $[12] !== t || $[13] !== translationCount || $[14] !== translationKey) {
    t10 = showSubmitError && <Alert action={<Link href={t("my:ctas.contactSupport.url")}>{t("my:ctas.contactSupport.text")}</Link>} className="mb-comfy-lg" dismissible={true} message={t("volunteers:actionDialog.error.message")} title={t("volunteers:actionDialog.error.title", {
      action: t(`${translationKey}.verb`),
      count: translationCount
    })} variant="negative" />;
    $[11] = showSubmitError;
    $[12] = t;
    $[13] = translationCount;
    $[14] = translationKey;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== checkboxGroupId || $[17] !== contactOptions || $[18] !== hasMultipleContacts || $[19] !== selectedContactIds || $[20] !== setSelectedContactIds || $[21] !== t) {
    t11 = hasMultipleContacts && <CheckboxGroupButtons className="pb-comfy-sm" hideLabel={true} label={t("volunteers:actionDialog.contactsLabel")} listClassName="grid grid-cols-1 gap-comfy-xs" onChange={setSelectedContactIds} value={selectedContactIds}>{contactOptions.map(t12 => {
        const {
          email,
          id,
          name
        } = t12;
        return <CheckboxGroupButtonsItem ariaLabel={name} id={`${checkboxGroupId}-${id}`} itemContentClassName="w-full pr-comfy-xl" key={id} value={id}><div className="flex flex-col gap-snug-md text-left"><span className="label-lg label-potent">{name}</span>{email && <span className="label-lg label-mild label-neutral-mild">{email}</span>}</div></CheckboxGroupButtonsItem>;
      })}</CheckboxGroupButtons>;
    $[16] = checkboxGroupId;
    $[17] = contactOptions;
    $[18] = hasMultipleContacts;
    $[19] = selectedContactIds;
    $[20] = setSelectedContactIds;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = `${translationKey}.secondary`;
  let t13;
  if ($[23] !== t || $[24] !== t12 || $[25] !== translationCount) {
    t13 = t(t12, {
      count: translationCount
    });
    $[23] = t;
    $[24] = t12;
    $[25] = translationCount;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <DialogActionClose>{t13}</DialogActionClose>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = isCancelAction ? "negative" : "neutral";
  const t16 = `${translationKey}.primary`;
  let t17;
  if ($[29] !== t || $[30] !== t16 || $[31] !== translationCount) {
    t17 = t(t16, {
      count: translationCount
    });
    $[29] = t;
    $[30] = t16;
    $[31] = translationCount;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== handleSubmit || $[34] !== isSubmitDisabled || $[35] !== t15 || $[36] !== t17) {
    t18 = <DialogActionPrimary disabled={isSubmitDisabled} onClick={handleSubmit} purpose={t15}>{t17}</DialogActionPrimary>;
    $[33] = handleSubmit;
    $[34] = isSubmitDisabled;
    $[35] = t15;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t14 || $[39] !== t18) {
    t19 = <DialogActions>{t14}{t18}</DialogActions>;
    $[38] = t14;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t19 || $[44] !== t3 || $[45] !== t4 || $[46] !== t5 || $[47] !== t7 || $[48] !== t9) {
    t20 = <DialogContent className={t3} icon={t4} purpose={t5} size="md" title={t7} supportingText={t9}>{t10}{t11}{t19}</DialogContent>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t19;
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t7;
    $[48] = t9;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== handleOpenChange || $[51] !== isBusy || $[52] !== isDialogOpen || $[53] !== t2 || $[54] !== t20) {
    t21 = <Dialog open={isDialogOpen} onOpenChange={handleOpenChange} isBusy={isBusy}>{t2}{t20}</Dialog>;
    $[50] = handleOpenChange;
    $[51] = isBusy;
    $[52] = isDialogOpen;
    $[53] = t2;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  return t21;
};
function _temp(shiftDetail) {
  return <span key={shiftDetail}>{shiftDetail}</span>;
}